import React, {useEffect} from "react";
import {observer} from "mobx-react-lite";
import {useStores} from "../../stores";
import {useLocation, useNavigate} from "react-router";
import {Button} from "@mui/material";
import {useAuth, useAuthUserOrNull, useLoginWithRedirect} from "@frontegg/react";
import axios from "axios";
import {MithraLoginResponse} from "../../services/ApiTypes";
import {environment} from "../../env";
import {isLoginRouteState, LOGIN_STATE_SESSION_KEY} from "../routing/LoginRouteState";
import {useRollbar} from "@rollbar/react";

export const FrontEggLoginButton: React.FC = observer(() => {
    const {authStore} = useStores();
    const navigate = useNavigate();
    const location = useLocation();
    const frontEggAuth = useAuth();
    const loginWithRedirect = useLoginWithRedirect();
    const rollbar = environment.rollbarConfig ? useRollbar() : undefined;

    const [stopProcessingLogin, setStopProcessingLogin] = React.useState(false);

    // This hook is designed to check whether the user is authenticated and allows to pull the user's data if they are
    const authUser = useAuthUserOrNull();

    const frontEggToken = frontEggAuth.user?.accessToken;
    const mithraUser = authStore.user;
    const isMithraLoggingIn = authStore.authentication.isMithraLoggingIn;
    const loginState = isLoginRouteState(location.state) ? location.state : null;
    const autoLogin = Boolean(loginState?.loginIntention?.autoLogin);

    const login = () => {
        (rollbar ?? console).debug('FrontEggLogin: starting login');

        setStopProcessingLogin(false);
        authStore.authentication.startLogin('front_egg');

        // Will send the user to FrontEgg and the user will return with a callback
        // so we need store the state somewhere to retrieve it later
        if (loginState) {
            sessionStorage.setItem(LOGIN_STATE_SESSION_KEY, JSON.stringify(loginState));
        } else {
            sessionStorage.removeItem(LOGIN_STATE_SESSION_KEY);
        }

        // Redirect the user
        loginWithRedirect()
            .catch(err => {
                (rollbar ?? console).debug('FrontEggLogin: loginWithRedirect() failed', err);
                authStore.authentication.processMithraLoginError(err);
            })
    };

    useEffect(() => {
        console.debug('FrontEggLoginButton.useEffect', {
            frontEggToken: Boolean(frontEggToken),
            authUser: Boolean(authUser),
            mithraUser: Boolean(mithraUser),
            isMithraLoggingIn,
            autoLogin,
            stopProcessingLogin,
        });
        if (stopProcessingLogin) return;

        if (frontEggToken && !mithraUser && !isMithraLoggingIn) {
            // Case: The frontegg token is received, and we can try to login to the Mithra platform.
            authStore.authentication.setIsMithraLoggingIn('front_egg');

            // Send the token to Mithra to get API access and finalize the login.
            const data = {access_token: frontEggToken};
            axios.post<MithraLoginResponse>(`${environment.authUrl}/login/frontegg/`, data)
                .then(resp => {
                    (rollbar ?? console).debug('FrontEggLogin: POST login/auth0 succeeded', resp.data?.user?.email);
                    authStore.authentication.processMithraLoginSuccess(resp.data, location, navigate, 'front_egg');
                })
                .catch(err => {
                    (rollbar ?? console).error('FrontEggLogin: POST login/auth0: failed', err);
                    // When we cannot login, we have to take action as the token from FrontEgg is still valid
                    // This might also happen when we loose internet connection, we don't want an infinite loop then

                    // // We can automatically logout from FrontEgg, in an attempt to invalidate the token
                    // const redirectToLogin: NavigationIntent = {navigate, route: routes.login};
                    // onFronteggLogout(frontEggAuth, redirectToLogin);

                    // For now we just stop the auto login
                    setStopProcessingLogin(true);
                    authStore.authentication.processMithraLoginError(err);
                })

            return;
        }
        if (autoLogin && !frontEggToken && !mithraUser && !isMithraLoggingIn) {
            login();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [frontEggToken, authUser, mithraUser, isMithraLoggingIn, autoLogin, stopProcessingLogin]);

    if (authStore.authentication.isLoggingIn === false) {
        return <Button
            variant="contained"
            onClick={() => login()}>
            Log in
            {environment.hasMultipleAuthProviders && <><br/>(2025 version)</>}
        </Button>
    } else {
        // keep silent
        return <></>
    }
});
